<template>
  <div class="bill-import-page">
    <div class="page-header">
      <div>
        <h2>账单导入</h2>
        <p>支持支付宝 CSV、微信 XLSX、京东 CSV。上传后先绑定账号，再确认导入。</p>
      </div>
    </div>

    <el-card class="upload-card" shadow="never">
      <el-form :inline="true" class="upload-form">
        <el-form-item label="账单来源">
          <el-select v-model="sourceType" class="source-select">
            <el-option label="自动识别" value="" />
            <el-option label="支付宝" value="alipay" />
            <el-option label="微信" value="wechat" />
            <el-option label="京东" value="jd" />
          </el-select>
        </el-form-item>
        <el-form-item label="账单文件">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            accept=".csv,.xlsx"
          >
            <el-button>选择文件</el-button>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="uploading" :disabled="!selectedFile" @click="handlePreview">
            上传预览
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card
      v-if="currentBatch"
      ref="batchCardRef"
      :class="['batch-card', { 'batch-card-highlight': detailHighlight }]"
      shadow="never"
    >
      <template #header>
        <div class="card-header">
          <span>当前批次 #{{ currentBatch.id }}：{{ currentBatch.filename }}</span>
          <el-tag :type="getStatusTag(currentBatch.status)">{{ getStatusText(currentBatch.status) }}</el-tag>
        </div>
      </template>

      <div class="stats-grid">
        <div class="stat-item">
          <span>总记录</span>
          <strong>{{ currentBatch.total_rows }}</strong>
        </div>
        <div class="stat-item income">
          <span>收入</span>
          <strong>¥{{ formatAmount(currentBatch.stats?.income || 0) }}</strong>
        </div>
        <div class="stat-item expense">
          <span>支出</span>
          <strong>¥{{ formatAmount(currentBatch.stats?.expense || 0) }}</strong>
        </div>
        <div class="stat-item neutral">
          <span>不计收支</span>
          <strong>{{ currentBatch.stats?.neutral_count || 0 }} 笔 / ¥{{ formatAmount(currentBatch.stats?.neutral || 0) }}</strong>
        </div>
      </div>

      <el-alert
        class="notice"
        type="info"
        show-icon
        :closable="false"
        title="不计收支记录会保留在批次中但默认跳过，不会自动生成转账，避免误记账。"
      />

      <h3>账号绑定</h3>
      <div class="mapping-list">
        <div v-for="item in currentBatch.external_accounts" :key="item.external_account_name" class="mapping-row">
          <div class="external-account">
            <strong>{{ item.external_account_name }}</strong>
            <span>{{ item.count }} 笔，需导入 {{ item.importable_count }} 笔</span>
          </div>
          <el-select
            v-model="accountMappings[item.external_account_name]"
            filterable
            :placeholder="item.importable_count ? '选择系统账号' : '仅不计收支，可不绑定'"
            class="account-select"
            :disabled="!item.importable_count"
          >
            <el-option-group
              v-for="group in accountGroups"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="account in group.accounts"
                :key="account.id"
                :label="getAccountOptionLabel(account)"
                :value="account.id"
              >
                <div class="account-option">
                  <span>{{ account.account_name }}</span>
                  <el-tag v-if="account.is_hidden" size="small" type="info">隐藏</el-tag>
                </div>
              </el-option>
            </el-option-group>
          </el-select>
        </div>
      </div>

      <div class="actions">
        <el-button
          type="primary"
          :loading="confirming"
          :disabled="currentBatch.status !== 'pending' || !mappingComplete"
          @click="handleConfirm"
        >
          确认导入
        </el-button>
        <el-button
          type="danger"
          plain
          :loading="reverting"
          :disabled="currentBatch.status !== 'imported'"
          @click="handleRevert(currentBatch)"
        >
          撤销本批次
        </el-button>
      </div>

      <h3>明细预览</h3>
      <el-table :data="currentBatch.rows || []" border stripe class="desktop-table">
        <el-table-column prop="transaction_date" label="时间" width="170" />
        <el-table-column prop="transaction_type" label="收/支" width="100">
          <template #default="{ row }">
            <el-tag :type="row.transaction_type === '收入' ? 'success' : row.transaction_type === '支出' ? 'danger' : 'info'">
              {{ row.transaction_type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120" align="right">
          <template #default="{ row }">¥{{ formatAmount(row.amount) }}</template>
        </el-table-column>
        <el-table-column prop="external_account_name" label="外部账号" min-width="180" />
        <el-table-column prop="counterparty" label="交易对方" min-width="160" />
        <el-table-column prop="product" label="商品/说明" min-width="260" show-overflow-tooltip />
        <el-table-column prop="status" label="导入状态" width="100" />
      </el-table>

      <div class="mobile-list">
        <div v-for="row in currentBatch.rows || []" :key="row.id" class="mobile-card">
          <div class="mobile-head">
            <el-tag :type="row.transaction_type === '收入' ? 'success' : row.transaction_type === '支出' ? 'danger' : 'info'" size="small">
              {{ row.transaction_type }}
            </el-tag>
            <strong>¥{{ formatAmount(row.amount) }}</strong>
          </div>
          <div>{{ row.transaction_date }}</div>
          <div>{{ row.external_account_name }}</div>
          <div>{{ row.counterparty || '-' }}</div>
          <p>{{ row.product || row.description }}</p>
        </div>
      </div>
    </el-card>

    <el-card class="history-card" shadow="never">
      <template #header>
        <span>导入历史</span>
      </template>
      <el-table :data="batches" border stripe>
        <el-table-column prop="id" label="批次" width="80" />
        <el-table-column prop="source_type" label="来源" width="90" />
        <el-table-column prop="filename" label="文件名" min-width="220" show-overflow-tooltip />
        <el-table-column prop="total_rows" label="记录" width="80" />
        <el-table-column prop="imported_count" label="已导入" width="90" />
        <el-table-column prop="skipped_count" label="跳过" width="80" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">{{ getStatusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220">
          <template #default="{ row }">
            <el-button size="small" @click="loadBatch(row.id)">查看</el-button>
            <el-button size="small" type="danger" plain :disabled="row.status !== 'imported'" @click="handleRevert(row)">
              撤销
            </el-button>
            <el-button size="small" type="danger" text :disabled="row.status !== 'pending'" @click="handleDeletePending(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '../api'

export default {
  name: 'BillImport',
  setup () {
    const store = useStore()
    const uploadRef = ref(null)
    const sourceType = ref('')
    const selectedFile = ref(null)
    const batchCardRef = ref(null)
    const detailHighlight = ref(false)
    const uploading = ref(false)
    const confirming = ref(false)
    const reverting = ref(false)
    const currentBatch = ref(null)
    const batches = ref([])
    const accountMappings = ref({})

    const accounts = computed(() => store.getters.activeAccounts)
    const accountGroups = computed(() => {
      const groups = new Map()
      accounts.value.forEach(account => {
        const primary = account.account_type || '未分类'
        const secondary = account.account_subcategory || '未设置二级分类'
        const visibility = account.is_hidden ? '隐藏账户' : '显示账户'
        const label = `${primary} / ${secondary} / ${visibility}`
        if (!groups.has(label)) {
          groups.set(label, [])
        }
        groups.get(label).push(account)
      })

      return Array.from(groups.entries())
        .sort(([left], [right]) => left.localeCompare(right, 'zh-CN'))
        .map(([label, groupAccounts]) => ({
          label,
          accounts: groupAccounts.sort((left, right) => {
            return String(left.account_name || '').localeCompare(String(right.account_name || ''), 'zh-CN')
          })
        }))
    })
    const mappingComplete = computed(() => {
      if (!currentBatch.value?.external_accounts?.length) return false
      return currentBatch.value.external_accounts.every(item => {
        return !item.importable_count || accountMappings.value[item.external_account_name]
      })
    })

    const handleFileChange = (file) => {
      selectedFile.value = file.raw
    }

    const handleFileRemove = () => {
      selectedFile.value = null
    }

    const handlePreview = async () => {
      if (!selectedFile.value) return
      uploading.value = true
      try {
        const formData = new FormData()
        formData.append('file', selectedFile.value)
        formData.append('source_type', sourceType.value)
        const response = await api.previewBillImport(formData)
        setCurrentBatch(response.data)
        await loadBatches()
        uploadRef.value?.clearFiles()
        selectedFile.value = null
        ElMessage.success('账单解析完成，请绑定账号后确认导入')
      } finally {
        uploading.value = false
      }
    }

    const setCurrentBatch = (batch) => {
      currentBatch.value = batch
      const mappings = {}
      ;(batch.external_accounts || []).forEach(item => {
        mappings[item.external_account_name] = item.account_id || null
      })
      accountMappings.value = mappings
    }

    const focusCurrentBatch = async () => {
      await nextTick()
      const element = batchCardRef.value?.$el || batchCardRef.value
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      detailHighlight.value = true
      window.setTimeout(() => {
        detailHighlight.value = false
      }, 1400)
    }

    const handleConfirm = async () => {
      confirming.value = true
      try {
        const response = await api.confirmBillImport(currentBatch.value.id, {
          account_mappings: accountMappings.value
        })
        setCurrentBatch(response.data)
        await loadBatches()
        await store.dispatch('fetchAccounts', { includeHidden: true })
        ElMessage.success(`导入完成：${response.data.imported_count} 笔，跳过 ${response.data.skipped_count} 笔`)
      } finally {
        confirming.value = false
      }
    }

    const handleRevert = async (batch) => {
      try {
        await ElMessageBox.confirm(
          `确定撤销批次 #${batch.id}？本批次导入的交易记录会被删除。`,
          '撤销确认',
          { type: 'warning', confirmButtonText: '撤销', cancelButtonText: '取消' }
        )
        reverting.value = true
        const response = await api.revertBillImport(batch.id)
        if (currentBatch.value?.id === batch.id) {
          setCurrentBatch(response.data)
        }
        await loadBatches()
        await store.dispatch('fetchAccounts', { includeHidden: true })
        ElMessage.success('已撤销本批次导入')
      } catch (error) {
        if (error !== 'cancel') throw error
      } finally {
        reverting.value = false
      }
    }

    const handleDeletePending = async (batch) => {
      try {
        await ElMessageBox.confirm(
          `确定删除待导入批次 #${batch.id}？删除后需要重新上传文件。`,
          '删除确认',
          { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
        )
        await api.deletePendingBillImport(batch.id)
        if (currentBatch.value?.id === batch.id) {
          currentBatch.value = null
          accountMappings.value = {}
        }
        await loadBatches()
        ElMessage.success('待导入批次已删除')
      } catch (error) {
        if (error !== 'cancel') throw error
      }
    }

    const loadBatch = async (id) => {
      const response = await api.getImportBatch(id)
      setCurrentBatch(response.data)
      await focusCurrentBatch()
      ElMessage.success(`已打开批次 #${response.data.id}`)
    }

    const loadBatches = async () => {
      const response = await api.getImportBatches()
      batches.value = response.data.items || []
    }

    const formatAmount = (value) => {
      return Number(value || 0).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const getAccountOptionLabel = (account) => {
      const hidden = account.is_hidden ? ' [隐藏]' : ''
      const subcategory = account.account_subcategory ? ` / ${account.account_subcategory}` : ''
      return `${account.account_name}${hidden} (${account.account_type || '未分类'}${subcategory})`
    }

    const getStatusText = (status) => {
      return {
        pending: '待导入',
        imported: '已导入',
        reverted: '已撤销'
      }[status] || status
    }

    const getStatusTag = (status) => {
      return {
        pending: 'warning',
        imported: 'success',
        reverted: 'info'
      }[status] || 'info'
    }

    onMounted(async () => {
      await store.dispatch('fetchAccounts', { includeHidden: true })
      await loadBatches()
    })

    return {
      uploadRef,
      sourceType,
      selectedFile,
      batchCardRef,
      detailHighlight,
      uploading,
      confirming,
      reverting,
      currentBatch,
      batches,
      accountMappings,
      accounts,
      accountGroups,
      mappingComplete,
      handleFileChange,
      handleFileRemove,
      handlePreview,
      handleConfirm,
      handleRevert,
      handleDeletePending,
      loadBatch,
      formatAmount,
      getAccountOptionLabel,
      getStatusText,
      getStatusTag
    }
  }
}
</script>

<style scoped>
.bill-import-page {
  min-height: 100%;
}

.page-header {
  margin-bottom: 18px;
}

.page-header h2 {
  margin: 0 0 6px;
  font-size: 26px;
  color: #263238;
}

.page-header p {
  margin: 0;
  color: #7b8794;
}

.upload-card,
.batch-card,
.history-card {
  margin-bottom: 18px;
  border-radius: 14px;
}

.batch-card {
  transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
  scroll-margin-top: 84px;
}

.batch-card-highlight {
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.18), 0 14px 34px rgba(64, 158, 255, 0.16);
  transform: translateY(-2px);
}

.source-select {
  width: 140px;
}

.card-header,
.actions,
.mobile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-item {
  padding: 14px;
  border-radius: 12px;
  background: #f6f8fb;
}

.stat-item span {
  display: block;
  color: #8a94a6;
  font-size: 12px;
  margin-bottom: 6px;
}

.stat-item strong {
  color: #2f3a4a;
  font-size: 18px;
}

.stat-item.income strong {
  color: #67c23a;
}

.stat-item.expense strong {
  color: #f56c6c;
}

.notice {
  margin-bottom: 18px;
}

h3 {
  margin: 18px 0 12px;
  color: #303133;
}

.mapping-list {
  display: grid;
  gap: 10px;
}

.mapping-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px);
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5eaf3;
  border-radius: 12px;
  background: #fbfcfe;
}

.external-account {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.external-account span {
  color: #909399;
  font-size: 12px;
}

.account-select {
  width: 100%;
}

.account-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.actions {
  justify-content: flex-start;
  margin: 18px 0;
}

.mobile-list {
  display: none;
}

@media (max-width: 768px) {
  .upload-form {
    display: block;
  }

  .source-select {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mapping-row {
    grid-template-columns: 1fr;
  }

  .desktop-table {
    display: none;
  }

  .mobile-list {
    display: grid;
    gap: 10px;
  }

  .mobile-card {
    padding: 12px;
    border-radius: 12px;
    border: 1px solid #e5eaf3;
    background: #fff;
    color: #606266;
  }

  .mobile-card p {
    margin: 8px 0 0;
    color: #303133;
  }
}
</style>
